// Loans hub (spec §4): alerts, the summary strip, applications in flight,
// running loans, pre-approved offers and the product catalogue.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronRight,
  faHandHoldingDollar,
} from '@fortawesome/free-solid-svg-icons';
import {
  getApplications,
  getLoanSummary,
  getLoans,
  getPreApprovedOffers,
  getProducts,
  deregisterInstantLoan,
  registerInstantLoan,
} from '@/api/loans';
import { ApiError } from '@/api/client';
import type { LoanProduct, LoanRecord, LoanSummary, PreApprovedOffer } from '@/types/loan.types';
import { productById } from '@/mock/loanData';
import { formatLong } from '@/mock/clock';
import { formatIndianCurrency } from '@/utils/currency';
import Loader from '@/components/ui/Loader/Loader';
import { EmptyLine, ErrorLine, LoanStatusChip, inr } from './loanShared';
import './loans.css';

const RUNNING_STATES = ['active', 'unutilised', 'overdue', 'moratorium', 'coolingOff', 'disbursed'];

const LoansHub = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<LoanProduct[] | null>(null);
  const [summary, setSummary] = useState<LoanSummary | null>(null);
  const [applications, setApplications] = useState<LoanRecord[]>([]);
  const [loans, setLoans] = useState<LoanRecord[]>([]);
  const [offers, setOffers] = useState<PreApprovedOffer[]>([]);
  const [error, setError] = useState('');
  const [deregisterMsg, setDeregisterMsg] = useState('');

  const load = () => {
    Promise.all([
      getProducts(),
      getLoanSummary(),
      getApplications(),
      getLoans(),
      getPreApprovedOffers(),
    ])
      .then(([p, s, a, l, o]) => {
        setProducts(p);
        setSummary(s);
        setApplications(a.filter((x) => x.state !== 'cancelled'));
        setLoans(l.filter((x) => RUNNING_STATES.includes(x.state)));
        setOffers(o.filter((x) => x.eligible));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load loans.'));
  };
  useEffect(load, []);

  const onDeregister = async () => {
    setError('');
    setDeregisterMsg('');
    try {
      await deregisterInstantLoan();
      setDeregisterMsg('You are deregistered from the instant loan programme.');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Deregistration failed.');
    }
  };

  const onRegister = async () => {
    setError('');
    setDeregisterMsg('');
    try {
      await registerInstantLoan();
      setDeregisterMsg('You are registered for the instant loan programme again.');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Registration failed.');
    }
  };

  if (!products || !summary) {
    return (
      <div className="nova-loans-page">
        {error ? <ErrorLine message={error} /> : <Loader rows={4} />}
      </div>
    );
  }

  return (
    <div className="nova-loans-page">
      <div className="nova-loans-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/dashboard')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Borrow</h2>
      </div>

      <div className="nova-loan-summary">
        <div className="nova-loan-summary-cell">
          <span className="nova-loan-summary-label">Total outstanding</span>
          <strong>{formatIndianCurrency(summary.totalOutstandingRupees)}</strong>
        </div>
        <div className="nova-loan-summary-cell">
          <span className="nova-loan-summary-label">Active loans</span>
          <strong>{summary.activeCount}</strong>
        </div>
        <div className="nova-loan-summary-cell">
          <span className="nova-loan-summary-label">Next EMI</span>
          <strong>
            {summary.nextEmi
              ? `${formatIndianCurrency(summary.nextEmi.amountRupees)} · ${formatLong(summary.nextEmi.date)}`
              : '—'}
          </strong>
        </div>
      </div>

      <div className="nova-loan-hub-cols">
        {applications.length > 0 && (
          <div>
            <h3 className="nova-loan-section">Applications</h3>
            {applications.map((a) => (
              <button
                type="button"
                key={a.id}
                className="nova-loan-row"
                onClick={() => navigate(`/borrow/applications/${a.id}`)}
              >
                <span className="nova-loan-row-text">
                  <span className="nova-loan-row-title">{productById(a.productId).name}</span>
                  <span className="nova-loan-row-sub">
                    {inr(a.amountPaise)} · applied {formatLong(a.appliedAt)}
                  </span>
                </span>
                <span className="nova-loan-row-right">
                  <LoanStatusChip state={a.state} />
                  <FontAwesomeIcon icon={faChevronRight} className="nova-loan-chevron" />
                </span>
              </button>
            ))}
          </div>
        )}

        {loans.length > 0 && (
          <div>
            <h3 className="nova-loan-section">Your loans</h3>
            {loans.map((l) => (
              <button
                type="button"
                key={l.id}
                className="nova-loan-row"
                onClick={() => navigate(`/borrow/loans/${l.id}`)}
              >
                <span className="nova-loan-row-text">
                  <span className="nova-loan-row-title">{productById(l.productId).name}</span>
                  <span className="nova-loan-row-sub">
                    {l.usedPaise !== undefined
                      ? `${inr(l.usedPaise)} used of ${inr(l.amountPaise)}`
                      : `${inr(l.outstandingPaise)} outstanding`}
                    {l.nextEmiDate ? ` · next EMI ${formatLong(l.nextEmiDate)}` : ''}
                  </span>
                </span>
                <span className="nova-loan-row-right">
                  <LoanStatusChip state={l.state} />
                  <FontAwesomeIcon icon={faChevronRight} className="nova-loan-chevron" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {offers.length > 0 && (
        <>
          <h3 className="nova-loan-section">Pre-approved for you</h3>
          {offers.map((o) => (
            <button
              type="button"
              key={o.productId}
              className="nova-loan-offer"
              onClick={() => navigate(`/borrow/apply/${o.productId}`)}
            >
              <span className="nova-loan-offer-icon">
                <FontAwesomeIcon icon={faHandHoldingDollar} />
              </span>
              <span className="nova-loan-row-text">
                <span className="nova-loan-row-title">{productById(o.productId).name}</span>
                <span className="nova-loan-row-sub">
                  Up to {formatIndianCurrency(o.amountRupees ?? 0)} — no extra documents
                </span>
              </span>
              <FontAwesomeIcon icon={faChevronRight} className="nova-loan-chevron" />
            </button>
          ))}
        </>
      )}

      <h3 className="nova-loan-section">All loans</h3>
      <div className="nova-loan-grid">
        {products.map((p) => (
          <button
            type="button"
            key={p.id}
            className="nova-loan-card"
            onClick={() => navigate(`/borrow/products/${p.id}`)}
          >
            <span className="nova-loan-card-name">{p.name}</span>
            <span className="nova-loan-card-tag">{p.tagline}</span>
            <span className="nova-loan-card-range">
              {formatIndianCurrency(p.minRupees)} – {formatIndianCurrency(p.maxRupees)}
            </span>
          </button>
        ))}
      </div>

      {summary.deregistered ? (
        <div className="nova-loan-deregister">
          <span>
            You are deregistered from the instant loan programme.
          </span>
          <button type="button" className="nova-loan-link" onClick={onRegister}>
            Register again
          </button>
        </div>
      ) : (
        <div className="nova-loan-deregister">
          <span>
            Instant loan programme — money in minutes, no documents.
          </span>
          <button type="button" className="nova-loan-link" onClick={onDeregister} disabled={summary.deregisterBlocked}>
            Deregister
          </button>
        </div>
      )}
      {deregisterMsg && <EmptyLine>{deregisterMsg}</EmptyLine>}
      <ErrorLine message={error} />
    </div>
  );
};

export default LoansHub;
